import { IDataGridColumnConfiguration, IDataGridValidator } from '@trackman/baseball-shared-components';
import { PositionDict } from 'constants/PositionDictionary';
import { DataKey } from 'enums/DataKey';
import { IError } from 'interfaces/review/IReviewDataRow';
import { ReviewDataRow } from 'models/review/ReviewDataRow/ReviewDataRow';
import { previousRowExist, previousRowIsInSameInningSide } from '../baserunnersValidatorHelpers';
import { isNotDuplicated, isNotSamePosition, playerHasNoEmptyGuid } from '../playerValidationHelpers';
import { getPlayerFromColumn } from './ValidationHelpers';

export const baserunnersValidator: IDataGridValidator = {
  validate: (rows: ReviewDataRow[], columns: IDataGridColumnConfiguration[]) => {
    const columnObj = {} as { [key in DataKey]: IDataGridColumnConfiguration };

    for (const column of columns) {
      columnObj[column.dataKey as DataKey] = column;
    }

    const { pitcher, batter, catcher, firstBase, secondBase, thirdBase } = columnObj;

    if (!pitcher || !batter || !catcher || !firstBase || !secondBase || !thirdBase) {
      return rows.map(() => []);
    }

    const result: IError[][] = [];
    let prevLivePlayIndex = -1;
    for (let i = 0; i < rows.length; i++) {
      const errors: IError[] = [];
      const row = rows[i];
      if (!row.isWarmup && !row.isDeleted) {
        let prevRow;

        if (previousRowExist(prevLivePlayIndex) && previousRowIsInSameInningSide(rows, row, prevLivePlayIndex)) {
          prevRow = rows[prevLivePlayIndex];
        }

        validateLogic(row, prevRow, errors, pitcher, batter, catcher, firstBase, secondBase, thirdBase);
        prevLivePlayIndex = i;
      }
      result.push(errors);
    }
    return result;
  },
};

const validateLogic = (
  row: ReviewDataRow,
  prevRow: ReviewDataRow | undefined,
  errors: IError[],
  pitcherColumn: IDataGridColumnConfiguration,
  batterColumn: IDataGridColumnConfiguration,
  catcherColumn: IDataGridColumnConfiguration,
  firstBaseColumn: IDataGridColumnConfiguration,
  secondBaseColumn: IDataGridColumnConfiguration,
  thirdBaseColumn: IDataGridColumnConfiguration
): IError[] => {
  const pitcher = getPlayerFromColumn(row, pitcherColumn);
  const batter = getPlayerFromColumn(row, batterColumn);
  const catcher = getPlayerFromColumn(row, catcherColumn);
  const firstBase = prevRow ? getPlayerFromColumn(prevRow, firstBaseColumn) : null;
  const secondBase = prevRow ? getPlayerFromColumn(prevRow, secondBaseColumn) : null;
  const thirdBase = prevRow ? getPlayerFromColumn(prevRow, thirdBaseColumn) : null;

  const defensivePositions = [pitcher, batter, catcher, firstBase, secondBase, thirdBase];

  defensivePositions.forEach((firstPlayer) => {
    if (!firstPlayer || !firstPlayer.id) {
      return;
    }

    defensivePositions.forEach((secondPlayer) => {
      if (!secondPlayer || !secondPlayer.id) {
        return;
      }

      const fpPosition = PositionDict[firstPlayer.defaultPosition];
      const spPosition = PositionDict[secondPlayer.defaultPosition];

      if (
        isNotSamePosition(firstPlayer, secondPlayer) &&
        isNotDuplicated(fpPosition, spPosition, errors) &&
        playerHasNoEmptyGuid(firstPlayer, secondPlayer)
      ) {
        errors.push({
          dataKey: `${fpPosition}-${spPosition}`,
          errorMessage: `${fpPosition} is same as ${spPosition}`,
        });
      }
    });
  });

  return errors;
};
